import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Hall from "@/pages/Hall";
import Room from "@/pages/Room";
import Add from "@/pages/Add";
import ItemDetail from "@/pages/ItemDetail";
import Collections from "@/pages/Collections";
import CollectionView from "@/pages/CollectionView";
import Commons from "@/pages/Commons";
import Directory from "@/pages/Directory";
import Profile from "@/pages/Profile";
import Requests from "@/pages/Requests";
import Friends from "@/pages/Friends";
import Search from "@/pages/Search";
import Settings from "@/pages/Settings";
import PublicCollection from "@/pages/PublicCollection";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="paper-grain min-h-screen flex items-center justify-center">
        <p className="font-mono-lc text-xs tracking-[0.24em] uppercase text-[var(--ink-dusty)]">opening the library…</p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }
  return <Layout>{children}</Layout>;
}

/** Public-facing page (no auth required). Keeps paper-grain background but
 *  hides the logged-in chrome. */
function PublicShell({ children }) {
  return (
    <div className="paper-grain min-h-screen px-6 py-10 sm:py-14">
      <div className="max-w-6xl mx-auto">{children}</div>
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/c/:token" element={<PublicShell><PublicCollection /></PublicShell>} />
      <Route path="/" element={<Protected><Hall /></Protected>} />
      <Route path="/room/:type" element={<Protected><Room /></Protected>} />
      <Route path="/add" element={<Protected><Add /></Protected>} />
      <Route path="/item/:id" element={<Protected><ItemDetail /></Protected>} />
      <Route path="/collections" element={<Protected><Collections /></Protected>} />
      <Route path="/collection/:id" element={<Protected><CollectionView /></Protected>} />
      <Route path="/commons" element={<Protected><Commons /></Protected>} />
      <Route path="/commons/directory" element={<Protected><Directory /></Protected>} />
      <Route path="/commons/requests" element={<Protected><Requests /></Protected>} />
      <Route path="/commons/friends" element={<Protected><Friends /></Protected>} />
      <Route path="/u/:username" element={<Protected><Profile /></Protected>} />
      <Route path="/search" element={<Protected><Search /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster
              position="bottom-center"
              toastOptions={{
                style: {
                  background: "#241F1B",
                  color: "#ECE1CD",
                  border: "1px solid rgba(179, 137, 90, 0.28)",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </div>
  );
}
